import React, { useEffect, useId, useRef, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsSelect from '../../common/DsSelect';
import StickyPageTopbar from '../../common/StickyPageTopbar';
import { ArticleSkeleton } from '../../common/ContentSkeletons';
import AiFluidMark from '../../common/chat/shared/AiFluidMark';
import EventMarketingBoard, { INITIATIVE_STATUS_LABELS } from './EventMarketingBoard';
import {
  formatCurrencyJPY,
  formatDateJP,
  formatDateTimeJST,
  formatItemCount,
  formatNumberJP,
} from '../../../utils/datetime';

export { INITIATIVE_STATUS_LABELS };

export const MARKETING_PHASE_OPTIONS = [
  { value: 'draft', label: '下書き' },
  { value: 'planning', label: '計画中' },
  { value: 'active', label: '実施中' },
  { value: 'review', label: '振り返り' },
  { value: 'complete', label: '完了' },
];

export const METRIC_STAGE_LABELS = {
  output: '実施量',
  outtake: '反応',
  outcome: '行動変化',
  impact: '事業成果',
};

const TABS = [
  { key: 'board', label: 'マーケティングボード' },
  { key: 'overview', label: '戦略' },
  { key: 'initiatives', label: '施策' },
  { key: 'logs', label: '全体の実施記録' },
  { key: 'metrics', label: '全体の効果' },
  { key: 'learnings', label: '学び' },
];

const LIST_CONFIG = {
  initiatives: {
    kind: 'initiative',
    title: '施策',
    description: '対象に届けるチャネル・訴求・期間・予算を記録します。',
    addLabel: '施策を追加',
    empty: '施策はまだ登録されていません。',
  },
  logs: {
    kind: 'log',
    title: '実施記録',
    description: '実際に行った内容と証跡、費用、結果メモを残します。',
    addLabel: '実施記録を追加',
    empty: '実施記録はまだありません。',
  },
  metrics: {
    kind: 'metric',
    title: '効果',
    description: '活動量・反応・行動・事業成果を段階別に記録します。',
    addLabel: '効果を追加',
    empty: '効果指標はまだ登録されていません。',
  },
  learnings: {
    kind: 'learning',
    title: '学び',
    description: '効いた条件と次回のアクションを、再利用できる形で残します。',
    addLabel: '学びを追加',
    empty: '学びはまだ登録されていません。',
  },
};

const nullableNumber = (value) => {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatYen = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return formatCurrencyJPY(value, { prefix: '', suffix: '円' });
};

const findInitiativeTitle = (initiatives, id) =>
  initiatives.find((item) => String(item.id || '') === String(id || ''))?.title || '';

const safeExternalUrl = (value) => {
  try {
    const url = new URL(String(value || '').trim());
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : '';
  } catch (_) {
    return '';
  }
};

function FieldError({ errors, path }) {
  const error = errors.find((item) => item.path === path || item.path?.endsWith(`.${path}`));
  return error?.message ? <p className="event-marketing-field-error">{error.message}</p> : null;
}

export function EventMarketingHeader({ view }) {
  const eventDate = formatDateJP(view.eventContext?.eventDate);
  const subtitle = [eventDate, view.eventContext?.name].filter(Boolean).join(' ・ ') || 'イベント';
  return (
    <StickyPageTopbar
      title="イベントマーケティング"
      actions={
        <>
          {view.canUseAi ? (
            <DsButton
              variant="secondary"
              size="sm"
              className="event-marketing-ai-button"
              onClick={view.openAiPlan}
            >
              <AiFluidMark variant="compact" />
              Casticart AI
            </DsButton>
          ) : null}
          {view.canEdit ? <EventMarketingActionsMenu view={view} /> : null}
          {!view.canEdit ? <span className="event-marketing-readonly">閲覧のみ</span> : null}
        </>
      }
    >
      <span className="page-topbar__subtitle">{subtitle}</span>
    </StickyPageTopbar>
  );
}

function EventMarketingActionsMenu({ view }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const firstItemRef = useRef(null);
  const menuId = `event-marketing-actions-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    if (!open) return undefined;
    firstItemRef.current?.focus();
    const closeOnOutsidePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      rootRef.current?.querySelector('[aria-haspopup="menu"]')?.focus();
    };
    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const runAction = (action) => {
    setOpen(false);
    action();
  };

  return (
    <div ref={rootRef} className="event-marketing-actions-menu">
      <DsButton
        variant="secondary"
        size="sm"
        icon
        aria-label="その他のマーケティング操作"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
      >
        <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true" />
      </DsButton>
      {open ? (
        <div id={menuId} className="event-marketing-actions-menu__panel ds-compact-action-menu" role="menu">
          <button
            ref={firstItemRef}
            type="button"
            role="menuitem"
            onClick={() => runAction(view.reuse.openCopy)}
          >
            <i className="fa-solid fa-copy" aria-hidden="true" />
            <span>他イベントからコピー</span>
          </button>
          <button type="button" role="menuitem" onClick={() => runAction(view.reuse.openTemplates)}>
            <i className="fa-solid fa-layer-group" aria-hidden="true" />
            <span>テンプレート</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function EventMarketingFooter({ view }) {
  return (
    <div className="page-back-row">
      <DsButton
        variant="secondary"
        size="sm"
        onClick={() => view.navigate(`/admin/workspaces/event-operations/events/${encodeURIComponent(view.eventId)}`)}
      >
        イベント詳細へ戻る
      </DsButton>
    </div>
  );
}

function EventMarketingNotices({ view }) {
  const reviewItems = Array.isArray(view.aiDraftReview?.items) ? view.aiDraftReview.items : [];
  const decisions = view.aiDraftReview?.decisions || {};
  const pendingItems = reviewItems.filter((item) => !decisions[item.id]);
  const acceptedCount = reviewItems.filter((item) => decisions[item.id] === 'accepted').length;
  return (
    <>
      {view.aiDraftStatus?.kind === 'ready' ? (
        <section className="event-marketing-ai-review" aria-labelledby="event-marketing-ai-review-title">
          <div className="event-marketing-ai-review__head">
            <div>
              <h2 id="event-marketing-ai-review-title">AI案を項目ごとに確認</h2>
              <p role="status">{view.aiDraftStatus.message}</p>
            </div>
            {pendingItems.length ? (
              <DsButton variant="secondary" size="sm" onClick={view.adoptAllAiDraftItems}>
                残りをすべて採用
              </DsButton>
            ) : null}
          </div>
          {reviewItems.length ? (
            <div className="event-marketing-ai-review__list">
              {reviewItems.map((item) => {
                const decision = decisions[item.id] || '';
                return (
                  <article key={item.id} className={`event-marketing-ai-review__item${decision ? ` is-${decision}` : ''}`}>
                    <div className="event-marketing-ai-review__diff">
                      <strong>{item.label}</strong>
                      <div>
                        <span><small>現在</small><b>{formatAiDiffValue(item.currentValue, item)}</b></span>
                        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                        <span><small>AI案</small><b>{formatAiDiffValue(item.proposedValue, item)}</b></span>
                      </div>
                    </div>
                    {decision ? (
                      <span className={`event-marketing-ai-review__decision is-${decision}`}>
                        <i className={`fa-solid ${decision === 'accepted' ? 'fa-check' : 'fa-minus'}`} aria-hidden="true" />
                        {decision === 'accepted' ? '採用済み' : '今回は採用しない'}
                      </span>
                    ) : (
                      <div className="event-marketing-ai-review__actions">
                        <DsButton variant="secondary" size="sm" onClick={() => view.discardAiDraftItem(item.id)}>採用しない</DsButton>
                        <DsButton size="sm" onClick={() => view.adoptAiDraftItem(item.id)}>採用</DsButton>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          ) : null}
          <div className="event-marketing-ai-review__footer">
            <p>採用してもまだ保存されません。既存項目を自動で削除することはありません。</p>
            {acceptedCount > 0 && view.canEdit ? (
              <DsButton size="sm" onClick={view.saveAiDraft} loading={view.saving} disabled={view.conflict}>
                採用した変更を保存
              </DsButton>
            ) : null}
          </div>
        </section>
      ) : view.aiDraftStatus ? (
        <div
          className="event-marketing-notice event-marketing-notice--danger"
          role="alert"
        >
          <div>
            <strong>AIの施策案を確認できませんでした</strong>
            <p>{view.aiDraftStatus.message}</p>
          </div>
        </div>
      ) : null}
      {view.conflict ? (
        <div className="event-marketing-notice event-marketing-notice--warning" role="alert">
          <div>
            <strong>他のユーザーによる更新があります</strong>
            <p>編集中の内容を確認し、最新データを再読み込みしてから再度保存してください。</p>
          </div>
          <DsButton variant="secondary" size="sm" onClick={view.reloadMarketing} disabled={view.loading || view.saving}>
            最新を再読み込み
          </DsButton>
        </div>
      ) : null}
      {view.validationErrors.length ? (
        <div className="event-marketing-notice event-marketing-notice--danger" role="alert">
          <strong>入力内容を確認してください</strong>
          <ul>
            {view.validationErrors.map((item, index) => (
              <li key={`${item.path}-${index}`}>{item.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

function formatAiDiffValue(value, item) {
  if (value === null || value === undefined || value === '') return '未設定';
  if (item?.kind === 'initiative' && value && typeof value === 'object') {
    return [value.title, value.channel, value.message].filter(Boolean).join(' ・ ') || '新しい施策';
  }
  if (item?.path === 'plannedBudget') return formatYen(value);
  if (typeof value === 'boolean') return value ? 'はい' : 'いいえ';
  return String(value);
}

function EventMarketingTabs({ view }) {
  return (
    <div className="event-marketing-tabs-scroll">
      <div className="segmented event-marketing-tabs" role="tablist" aria-label="マーケティング情報">
        {TABS.map((tab) => {
          const count = ['board', 'overview'].includes(tab.key) ? null : view.marketing?.[tab.key]?.length || 0;
          const active = view.activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              className={`event-marketing-tab${active ? ' is-active' : ''}`}
              role="tab"
              aria-selected={active}
              onClick={() => view.setActiveTab(tab.key)}
            >
              {tab.label}{count !== null ? ` ${formatItemCount(count, '0件')}` : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EventMarketingOverview({ view }) {
  const marketing = view.marketing;
  return (
    <section className="event-marketing-card" role="tabpanel" aria-label="概要・戦略">
      <div className="event-marketing-card__head">
        <div>
          <h2>概要・戦略</h2>
          <p>目的、対象、仮説、主な訴求と成功基準を最初に揃えます。入力内容は保存するまで下書きです。</p>
        </div>
        {view.canEdit ? (
          <DsButton size="sm" onClick={view.saveOverview} loading={view.saving} disabled={view.conflict}>
            保存
          </DsButton>
        ) : null}
      </div>

      <div className="event-marketing-form-grid">
        <label className="event-marketing-field">
          <span>フェーズ</span>
          <DsSelect
            value={marketing.phase}
            disabled={!view.canEdit}
            onChange={(event) => view.updateOverview('phase', event.target.value)}
          >
            {MARKETING_PHASE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </DsSelect>
          <FieldError errors={view.validationErrors} path="phase" />
        </label>

        <label className="event-marketing-field event-marketing-field--wide">
          <span>目的</span>
          <textarea
            rows={3}
            value={marketing.objective}
            disabled={!view.canEdit}
            placeholder="例：新規来場者を増やし、次回公演へつながる顧客基盤を作る"
            onChange={(event) => view.updateOverview('objective', event.target.value)}
          />
          <FieldError errors={view.validationErrors} path="objective" />
        </label>

        <label className="event-marketing-field">
          <span>主な対象</span>
          <textarea
            rows={3}
            value={marketing.audience}
            disabled={!view.canEdit}
            placeholder="誰に届けるか"
            onChange={(event) => view.updateOverview('audience', event.target.value)}
          />
          <FieldError errors={view.validationErrors} path="audience" />
        </label>

        <label className="event-marketing-field">
          <span>対象への洞察</span>
          <textarea
            rows={3}
            value={marketing.audienceInsight}
            disabled={!view.canEdit}
            placeholder="対象の動機、障壁、判断材料"
            onChange={(event) => view.updateOverview('audienceInsight', event.target.value)}
          />
          <FieldError errors={view.validationErrors} path="audienceInsight" />
        </label>

        <label className="event-marketing-field event-marketing-field--wide">
          <span>中心メッセージ</span>
          <textarea
            rows={2}
            value={marketing.keyMessage}
            disabled={!view.canEdit}
            placeholder="対象に最も伝えたい価値"
            onChange={(event) => view.updateOverview('keyMessage', event.target.value)}
          />
          <FieldError errors={view.validationErrors} path="keyMessage" />
        </label>
      </div>

      <div className="event-marketing-subsection">
        <h3>主要KPI</h3>
        <div className="event-marketing-kpi-grid">
          <label className="event-marketing-field">
            <span>指標名</span>
            <input
              value={marketing.primaryKpi.name}
              disabled={!view.canEdit}
              placeholder="例：チケット販売数"
              onChange={(event) => view.updatePrimaryKpi('name', event.target.value)}
            />
          </label>
          <label className="event-marketing-field">
            <span>基準値</span>
            <input
              type="number"
              value={marketing.primaryKpi.baseline ?? ''}
              disabled={!view.canEdit}
              onChange={(event) => view.updatePrimaryKpi('baseline', nullableNumber(event.target.value))}
            />
          </label>
          <label className="event-marketing-field">
            <span>目標値</span>
            <input
              type="number"
              value={marketing.primaryKpi.target ?? ''}
              disabled={!view.canEdit}
              onChange={(event) => view.updatePrimaryKpi('target', nullableNumber(event.target.value))}
            />
          </label>
          <label className="event-marketing-field">
            <span>単位</span>
            <input
              value={marketing.primaryKpi.unit}
              disabled={!view.canEdit}
              placeholder="枚、円、% など"
              onChange={(event) => view.updatePrimaryKpi('unit', event.target.value)}
            />
          </label>
        </div>
        <FieldError errors={view.validationErrors} path="primaryKpi" />
      </div>

      <div className="event-marketing-form-grid">
        <label className="event-marketing-field">
          <span>計画予算</span>
          <div className="event-marketing-affixed-input">
            <input
              type="number"
              min="0"
              value={marketing.plannedBudget ?? ''}
              disabled={!view.canEdit}
              onChange={(event) => view.updateOverview('plannedBudget', nullableNumber(event.target.value))}
            />
            <span>円</span>
          </div>
          <FieldError errors={view.validationErrors} path="plannedBudget" />
        </label>
        <label className="event-marketing-field">
          <span>補足メモ</span>
          <textarea
            rows={3}
            value={marketing.notes}
            disabled={!view.canEdit}
            placeholder="前提条件、制約、判断が必要なこと"
            onChange={(event) => view.updateOverview('notes', event.target.value)}
          />
        </label>
      </div>
    </section>
  );
}

function RecordBody({ tab, item, marketing, adminCandidates }) {
  if (tab === 'initiatives') {
    const owner = adminCandidates.find((candidate) => Number(candidate.id ?? candidate.admin_user_id) === Number(item.ownerAdminId));
    const ownerName = owner?.display_name || owner?.full_name || owner?.nickname || owner?.username || '';
    return (
      <>
        <div className="event-marketing-record__title-row">
          <h3>{item.title || '名称未設定'}</h3>
          <span className={`event-marketing-status is-${item.status}`}>{INITIATIVE_STATUS_LABELS[item.status] || item.status}</span>
        </div>
        <p>{[item.channel, item.message].filter(Boolean).join(' ・ ') || 'チャネル・訴求未設定'}</p>
        <dl>
          <div><dt>{item.scheduleKind === 'single' ? '実施日時' : '期間'}</dt><dd>{item.scheduleKind === 'single' ? (formatDateTimeJST(item.scheduledAt) || '未設定') : ([formatDateJP(item.startOn), formatDateJP(item.endOn)].filter(Boolean).join(' 〜 ') || '未設定')}</dd></div>
          <div><dt>担当</dt><dd>{ownerName || '未設定'}</dd></div>
          <div><dt>計画予算</dt><dd>{formatYen(item.plannedBudget)}</dd></div>
          <div><dt>実績費用</dt><dd>{formatYen(item.actualBudget)}</dd></div>
          <div><dt>メモ</dt><dd>{item.notes || '未記録'}</dd></div>
        </dl>
      </>
    );
  }
  if (tab === 'logs') {
    const initiativeTitle = findInitiativeTitle(marketing.initiatives, item.initiativeId);
    const evidenceUrl = safeExternalUrl(item.evidenceUrl);
    return (
      <>
        <div className="event-marketing-record__title-row"><h3>{item.summary || '内容未設定'}</h3></div>
        <p>{[formatDateTimeJST(item.occurredAt), initiativeTitle].filter(Boolean).join(' ・ ') || '日時未設定'}</p>
        <dl>
          <div><dt>費用</dt><dd>{formatYen(item.spend)}</dd></div>
          <div><dt>結果</dt><dd>{item.resultNote || '未記録'}</dd></div>
          <div>
            <dt>証跡</dt>
            <dd>{evidenceUrl ? <a href={evidenceUrl} target="_blank" rel="noopener noreferrer">証跡を開く</a> : '未記録'}</dd>
          </div>
        </dl>
      </>
    );
  }
  if (tab === 'metrics') {
    const initiativeTitle = findInitiativeTitle(marketing.initiatives, item.initiativeId);
    return (
      <>
        <div className="event-marketing-record__title-row">
          <h3>{item.name || '指標名未設定'}</h3>
          <span className="event-marketing-status">{METRIC_STAGE_LABELS[item.stage] || item.stage}</span>
        </div>
        <p className="event-marketing-record__metric">{formatNumberJP(item.value)} {item.unit || ''}</p>
        <dl><div><dt>観測日</dt><dd>{formatDateJP(item.observedOn) || '未設定'}</dd></div><div><dt>施策</dt><dd>{initiativeTitle || '全体'}</dd></div><div><dt>出典</dt><dd>{item.source || '未記録'}</dd></div><div><dt>補足</dt><dd>{item.note || '未記録'}</dd></div></dl>
      </>
    );
  }
  return (
    <>
      <div className="event-marketing-record__title-row">
        <h3>{item.summary || '内容未設定'}</h3>
        {item.reusable ? <span className="event-marketing-status is-done">次回も活用</span> : null}
      </div>
      <p>{item.evidence || '根拠・観察は未記録です。'}</p>
      <dl><div><dt>成立条件</dt><dd>{item.conditions || '未記録'}</dd></div><div><dt>次回</dt><dd>{item.nextAction || '未記録'}</dd></div></dl>
    </>
  );
}

function EventMarketingList({ view, tab }) {
  const config = LIST_CONFIG[tab];
  const items = view.marketing[tab];
  return (
    <section className="event-marketing-card" role="tabpanel" aria-label={config.title}>
      <div className="event-marketing-card__head">
        <div><h2>{config.title}</h2><p>{config.description}</p></div>
        {view.canEdit ? (
          <div className="event-marketing-card__actions">
            {tab === 'metrics' ? (
              <DsButton variant="secondary" size="sm" onClick={view.addFunnelMetrics}>
                販売ファネルの記録項目を追加
              </DsButton>
            ) : null}
            {tab === 'metrics' && view.isDirty ? (
              <DsButton size="sm" onClick={view.saveMarketingChanges} loading={view.saving} disabled={view.conflict}>
                変更を保存
              </DsButton>
            ) : null}
            <DsButton size="sm" onClick={() => view.openEditor(config.kind)}>{config.addLabel}</DsButton>
          </div>
        ) : null}
      </div>
      {items.length ? (
        <div className="event-marketing-record-list">
          {items.map((item, index) => (
            <article
              className="event-marketing-record"
              key={item.id || `${tab}-${index}`}
              {...(tab === 'initiatives' && item.id ? { 'data-marketing-initiative-id': item.id } : {})}
            >
              <div className="event-marketing-record__body">
                <RecordBody tab={tab} item={item} marketing={view.marketing} adminCandidates={view.adminCandidates} />
              </div>
              {view.canEdit || (tab === 'initiatives' && item.id && view.canViewTasks) ? (
                <div className="event-marketing-record__actions">
                  {tab === 'initiatives' && item.id && view.canViewTasks ? (
                    <DsButton variant="secondary" size="sm" onClick={() => view.reuse.openTask(item)}>
                      {view.canEdit ? 'タスクに追加・関連タスク' : '関連タスク'}
                    </DsButton>
                  ) : null}
                  {view.canEdit ? (
                    <DsButton variant="secondary" size="sm" onClick={() => view.openEditor(config.kind, index)}>編集</DsButton>
                  ) : null}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <div className="event-marketing-empty"><p>{config.empty}</p></div>
      )}
    </section>
  );
}

export default function EventMarketingPageParts({ view }) {
  return (
    <>
      <EventMarketingHeader view={view} />
      <div className="page-topbar-body">
        <div className="event-marketing-shell">
          <EventMarketingNotices view={view} />
          {view.loading ? (
        <section className="event-marketing-card"><ArticleSkeleton lines={10} /></section>
      ) : view.loadError ? (
        <section className="event-marketing-card event-marketing-load-error" role="alert">
          <h2>マーケティング情報を読み込めませんでした</h2>
          <p>{view.loadError}</p>
          <DsButton variant="secondary" size="sm" onClick={view.reloadMarketing}>再読み込み</DsButton>
        </section>
      ) : view.marketing ? (
        <>
          <EventMarketingTabs view={view} />
          {view.isDirty && view.canEdit ? (
            <div className="event-marketing-save-bar" role="status">
              <div>
                <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                <span><strong>未保存の変更があります</strong><small>保存するまで他の画面やAIには反映されません。</small></span>
              </div>
              <DsButton size="sm" onClick={view.saveMarketingChanges} loading={view.saving} disabled={view.conflict}>
                変更を保存
              </DsButton>
            </div>
          ) : null}
          {view.activeTab === 'board'
            ? <EventMarketingBoard view={view} />
            : view.activeTab === 'overview'
              ? <EventMarketingOverview view={view} />
              : <EventMarketingList view={view} tab={view.activeTab} />}
        </>
      ) : null}
          <EventMarketingFooter view={view} />
        </div>
      </div>
    </>
  );
}
